import { ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isBefore } from 'date-fns';
import { AlertTriangle, CircleCheck, Clock, ShieldAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';

interface Props { onNavigate: (view: string) => void }

interface Project { id: string; external_id: string | null; name: string; health: string | null; progress: number | null; budget: number | null; spent: number | null; start_date: string | null; end_date: string | null }
interface Task { id: string; external_id: string | null; project_external_id: string | null; title: string; status: string | null; due_date: string | null }
interface Risk { id: string; entity_type: string; entity_id: string; rule_id: string; severity: string; detected_at: string }

const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const HEALTH_RANK: Record<string, number> = { delayed: 3, 'at-risk': 2, 'on-track': 1 };
const HEALTH_LABEL: Record<string, string> = { delayed: 'Delayed', 'at-risk': 'At risk', 'on-track': 'On track' };
const RULE_LABELS: Record<string, string> = {
  release_health_degraded: 'Release health degraded', blocked_dependency: 'Blocked dependency', overdue_not_done: 'Overdue and not done',
};

const compact = (n: number) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
const pct = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** Share of the planned schedule already elapsed (0-100), or null without dates. */
function elapsedPct(p: Project, now: number): number | null {
  if (!p.start_date || !p.end_date) return null;
  const s = new Date(p.start_date).getTime(); const e = new Date(p.end_date).getTime();
  return e > s ? pct(((now - s) / (e - s)) * 100) : null;
}

const HealthPill = ({ health }: { health: string | null }) => {
  const { t } = useLocalization();
  const h = health ?? 'unknown';
  const Icon = h === 'on-track' ? CircleCheck : h === 'delayed' ? AlertTriangle : Clock;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wide',
      h === 'on-track' ? 'bg-success/15 text-foreground' : h === 'delayed' ? 'bg-destructive/10 text-destructive' : 'bg-warning/20 text-foreground')}>
      <Icon className="h-3 w-3" aria-hidden="true" />{HEALTH_LABEL[h] ?? t('No status')}
    </span>
  );
};

const Bar = ({ value, label, tone = 'primary' }: { value: number; label: string; tone?: 'primary' | 'alert' | 'muted' }) => {
  const { tx } = useLocalization();
  return (
  <span className="block h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={tx(label)}>
    <span className={cn('block h-full rounded-full', tone === 'alert' ? 'bg-destructive' : tone === 'muted' ? 'bg-muted-foreground/50' : 'bg-primary')} style={{ width: `${value}%` }} />
  </span>
);
};

const Panel = ({ title, icon, action, onAction, children }: { title: string; icon: ReactNode; action?: string; onAction?: () => void; children: ReactNode }) => {
  const { tx } = useLocalization();
  return (
  <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-primary" aria-hidden="true">{icon}</span>
      <h2 className="font-heading text-base font-semibold">{tx(title)}</h2>
      {action && onAction && <button type="button" onClick={onAction} className="ms-auto rounded text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{action}</button>}
    </div>
    {children}
  </section>
);
};

/** Delivery: are we on track, and what could go wrong. Real projects, tasks and risk signals. */
const DeliveryOverview = ({ onNavigate }: Props) => {
  const { tx, t, ago } = useLocalization();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['delivery-overview'],
    staleTime: 60_000,
    queryFn: async () => {
      const [p, t, r] = await Promise.all([
        supabase.from('projects_sync').select('id,external_id,name,health,progress,budget,spent,start_date,end_date'),
        supabase.from('tasks_sync').select('id,external_id,project_external_id,title,status,due_date').limit(1000),
        supabase.from('risk_signals').select('id,entity_type,entity_id,rule_id,severity,detected_at').is('resolved_at', null).order('detected_at', { ascending: false }).limit(100),
      ]);
      if (p.error) throw new Error(p.error.message);
      if (t.error) throw new Error(t.error.message);
      if (r.error) throw new Error(r.error.message);
      return { projects: (p.data ?? []) as Project[], tasks: (t.data ?? []) as Task[], risks: (r.data ?? []) as Risk[] };
    },
  });

  const v = useMemo(() => {
    const now = Date.now();
    const projects = data?.projects ?? []; const tasks = data?.tasks ?? []; const risks = data?.risks ?? [];
    const byExt = new Map(projects.filter((p) => p.external_id).map((p) => [p.external_id as string, p]));
    const riskProject = (r: Risk): Project | undefined => {
      if (r.entity_type === 'project') return projects.find((p) => p.id === r.entity_id || p.external_id === r.entity_id);
      const ext = tasks.find((t) => t.id === r.entity_id || t.external_id === r.entity_id)?.project_external_id;
      return ext ? byExt.get(ext) : undefined;
    };
    const cards = projects.map((p) => {
      const mine = tasks.filter((t) => t.project_external_id === p.external_id);
      const done = mine.filter((t) => t.status === 'done').length;
      const blocked = mine.filter((t) => t.status === 'blocked').length;
      const overdue = mine.filter((t) => t.status !== 'done' && t.due_date && isBefore(new Date(t.due_date), new Date(now))).length;
      const pr = risks.filter((r) => riskProject(r)?.id === p.id);
      const worst = pr.reduce((m, r) => Math.max(m, SEV_RANK[r.severity] ?? 0), 0);
      const progress = pct(p.progress ?? 0);
      const elapsed = elapsedPct(p, now);
      const budgetUsed = p.budget && p.budget > 0 && p.spent != null ? pct((p.spent / p.budget) * 100) : null;
      return { p, total: mine.length, done, blocked, overdue, risks: pr.length, worst, progress, elapsed, budgetUsed,
        behindSchedule: elapsed != null && elapsed - progress > 15, overBudget: budgetUsed != null && budgetUsed - progress > 15 };
    }).sort((a, b) => (HEALTH_RANK[b.p.health ?? ''] ?? 0) - (HEALTH_RANK[a.p.health ?? ''] ?? 0) || b.worst - a.worst || a.p.name.localeCompare(b.p.name));
    const count = (h: string) => projects.filter((p) => p.health === h).length;
    const blockedTasks = tasks.filter((t) => t.status === 'blocked');
    const overdueTasks = tasks.filter((t) => t.status !== 'done' && t.due_date && isBefore(new Date(t.due_date), new Date(now)));
    const watch = [...risks].sort((a, b) => (SEV_RANK[b.severity] ?? 0) - (SEV_RANK[a.severity] ?? 0)).slice(0, 5)
      .map((r) => ({ r, project: riskProject(r) }));
    return { cards, onTrack: count('on-track'), atRisk: count('at-risk'), delayed: count('delayed'), risks: risks.length, blocked: blockedTasks.length, overdue: overdueTasks.length, watch, blockedTasks: blockedTasks.slice(0, 4), byExt };
  }, [data]);

  if (isLoading) return <div className="space-y-4" aria-busy="true"><Skeleton className="h-20 w-full" /><div className="grid gap-4 md:grid-cols-2"><Skeleton className="h-48" /><Skeleton className="h-48" /></div></div>;
  if (isError) return <p role="alert" className="text-sm text-muted-foreground">{t('Delivery data could not be loaded. Try again shortly.')}</p>;
  if (v.cards.length === 0) return <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">{t('No projects yet. Connect Jira, Azure DevOps or import a project file and delivery health will appear here.')}</p>;

  const Stat = ({ label, value, tone }: { label: string; value: number; tone?: 'alert' | 'ok' }) => {
  const { tx } = useLocalization();
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
      <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{tx(label)}</p>
      <p className={cn('mt-1 font-heading text-2xl font-bold', tone === 'alert' && value > 0 && 'text-destructive')}>{value}</p>
    </div>
  );
};

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label={t('On track')} value={v.onTrack} />
        <Stat label={t('At risk')} value={v.atRisk} />
        <Stat label={t('Delayed')} value={v.delayed} tone="alert" />
        <Stat label={t('Open risks')} value={v.risks} tone="alert" />
        <Stat label={t('Blocked tasks')} value={v.blocked} tone="alert" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid gap-4 md:grid-cols-2">
          {v.cards.map((c) => (
            <button key={c.p.id} type="button" onClick={() => onNavigate('projects')}
              className="rounded-lg border border-border bg-card p-4 text-start shadow-sm hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="flex items-start gap-2">
                <span className="min-w-0 flex-1 font-heading text-[15px] font-semibold leading-snug">{c.p.name}</span>
                <HealthPill health={c.p.health} />
              </span>
              <span className="mt-3 block space-y-2.5">
                <span className="block">
                  <span className="mb-1 flex justify-between gap-2 text-xs text-muted-foreground"><span>{t('Progress')}</span><span className="font-mono">{c.progress}%</span></span>
                  <Bar value={c.progress} label={t('{0} progress', { 0: c.p.name })} />
                </span>
                {c.elapsed != null && (
                  <span className="block">
                    <span className="mb-1 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                      <span className="min-w-0 truncate">{t('Schedule used')}{c.p.end_date ? t(' · due {0}', { 0: format(new Date(c.p.end_date), 'd MMM yyyy') }) : ''}</span>
                      <span className={cn('shrink-0 whitespace-nowrap font-mono', c.behindSchedule && 'font-semibold text-destructive')}>{c.elapsed}%{c.behindSchedule ? t(' · behind plan') : ''}</span>
                    </span>
                    <Bar value={c.elapsed} label={t('{0} schedule elapsed', { 0: c.p.name })} tone={c.behindSchedule ? 'alert' : 'muted'} />
                  </span>
                )}
                {c.budgetUsed != null && c.p.spent != null && c.p.budget != null && (
                  <span className="block">
                    <span className="mb-1 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                      <span className="min-w-0 truncate">{t('Budget ·')} {compact(c.p.spent)}  {t('of')} {compact(c.p.budget)}</span>
                      <span className={cn('shrink-0 whitespace-nowrap font-mono', c.overBudget && 'font-semibold text-destructive')}>{c.budgetUsed}%{c.overBudget ? t(' · over pace') : ''}</span>
                    </span>
                    <Bar value={c.budgetUsed} label={t('{0} budget used', { 0: c.p.name })} tone={c.overBudget ? 'alert' : 'muted'} />
                  </span>
                )}
              </span>
              <span className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
                <span>{c.done}/{c.total}  {t('tasks done')}</span>
                {c.blocked > 0 && <span className="font-semibold text-destructive">{c.blocked}  {t('blocked')}</span>}
                {c.overdue > 0 && <span className="font-semibold text-destructive">{c.overdue}  {t('overdue')}</span>}
                <span>{c.risks}  {t('open risk')}{c.risks === 1 ? '' : 's'}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="space-y-5">
          <Panel title={t('Risk watchlist')} icon={<ShieldAlert className="h-5 w-5" />} action={t('All risks')} onAction={() => onNavigate('risks')}>
            {v.watch.length === 0 ? <p className="text-sm text-muted-foreground">{t('No open risks.')}</p> : (
              <ul className="space-y-2">
                {v.watch.map(({ r, project }) => (
                  <li key={r.id} className="rounded-md border border-border px-3 py-2">
                    <p className="flex items-start gap-2 text-sm font-semibold">
                      <span className="min-w-0 flex-1">{RULE_LABELS[r.rule_id] ?? r.rule_id.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-[11px] uppercase text-muted-foreground">{tx(r.severity)}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{project?.name ?? t('Unlinked')}  {t('· detected')} {ago(r.detected_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title={t('Blocked tasks')} icon={<AlertTriangle className="h-5 w-5" />} action={t('Projects')} onAction={() => onNavigate('projects')}>
            {v.blockedTasks.length === 0 ? <p className="text-sm text-muted-foreground">{t('Nothing is blocked.')}</p> : (
              <ul className="space-y-1.5 text-sm">
                {v.blockedTasks.map((t) => (
                  <li key={t.id}><span className="font-semibold">{tx(t.title)}</span>{t.project_external_id && v.byExt.get(t.project_external_id) ? <span className="text-xs text-muted-foreground"> · {v.byExt.get(t.project_external_id)?.name}</span> : null}</li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOverview;
